import queryString from 'query-string'
import { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'

import { useAuth } from 'components/AuthProvider'
import { buyPath, dashboardPath, sellPath } from 'components/Routes'
import DefaultLayout from 'components/layout/DefaultLayout'
import { MIN_QUANTITY } from 'utils/quantitySlider'
import { useMutateCreateReviewOrder } from 'hooks/useTrading'
import PauseTrading from '../PauseTrading/PauseTrading'

export default function CreateReviewOrder(): JSX.Element {
  const { search } = useLocation()
  const navigate = useNavigate()

  const { type, productId: productIdStr } = queryString.parse(search)
  const productId = Number(productIdStr)

  const { setToast } = useAuth()

  const [tradingPaused, setTradingPaused] = useState<boolean>(false)

  const createReviewOrder = useMutateCreateReviewOrder()

  useEffect(() => {
    if (!type) {
      navigate(dashboardPath())
    } else if ((type === 'buy' || type === 'sell') && productId) {
      createReviewOrder.mutate(
        {
          productId,
          orderType: type as 'buy' | 'sell',
          quantityMg: MIN_QUANTITY
        },
        {
          onSuccess: data => {
            // Guard against a null/empty payload (e.g. an unwrapped failure
            // envelope) — without an id there's no review order to navigate to.
            if (!data?.id) {
              setToast({
                open: true,
                message: 'Could not create review order. Please try again.',
                type: 'error',
                duration: 3000
              })
              return
            }
            setTradingPaused(false)
            if (type === 'buy') {
              navigate(
                buyPath({
                  routeParams: { reviewOrderId: data.id },
                  queryParams: { productId }
                }),
                { replace: true }
              )
            } else {
              navigate(
                sellPath({
                  routeParams: { reviewOrderId: data.id },
                  queryParams: { productId }
                }),
                { replace: true }
              )
            }
          },
          onError: (error: unknown) => {
            const err = error as { message?: string; errorStatus?: string }
            if (err?.errorStatus === 'trading_is_paused') {
              setTradingPaused(true)
            }
            setToast({
              open: true,
              message: err?.message ?? 'An error occurred',
              type: 'error',
              duration: 3000
            })
          }
        }
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, productId])

  if (tradingPaused) {
    return <PauseTrading />
  }

  return (
    <DefaultLayout heading={type === 'buy' ? 'Buy' : 'Sell'} loading>
      {/* NOTE: as loading wrapper is always loading, this will never be seen */}
      <p>Creating review order...</p>
    </DefaultLayout>
  )
}
